import React from 'react';
import { useTranslation } from 'react-i18next';
import { translateBlockValue } from 'i18n';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { capitalize } from 'helpers/helpers';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import {
    INSCRIPTION_ORIGINAL_AMOUNT,
    PROJECT_ID,
} from 'routes/BudgetModule/config/constants';
import { STALE_TIME } from 'business/constants';
import {
    getAmountFieldForPhase,
    getCommentForPhase,
} from 'routes/BudgetModule/shared/phases/helpers';
import {
    Project,
    useGetProjects,
} from 'routes/BudgetModule/shared/useGetProjects';
import { filterAssignedProjects } from '../../cellRenderers/ProjectsCellRenderer/ProjectsHelpers';

type Props = {
    inscriptionData: FlowAnyObject;
    budgetProposal: BudgetProposalT;
};

const StyAmounts = styled('div', {
    name: 'StyAmounts',
})({
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '0.5rem',
    gap: '1rem',
});

const StyTextField = styled(TextField, {
    name: 'StyTextField',
})({
    '& input': {
        fontSize: '12px',
    },
    '& label': {
        fontSize: '12px',
    },
});

export default function Overview(props: Props): React.ReactElement {
    const { t } = useTranslation();
    const { inscriptionData, budgetProposal } = props;

    const phaseMetric = getAmountFieldForPhase(budgetProposal.phase);
    const phaseMetricAmount = inscriptionData[phaseMetric];

    const comparisonMetric = INSCRIPTION_ORIGINAL_AMOUNT;
    const comparisonMetricAmount = inscriptionData[comparisonMetric];

    const phaseComment = getCommentForPhase(budgetProposal.phase);
    // TODO StrictNullCheck : Try to remove "!"
    const phaseCommentValue = inscriptionData[phaseComment!];

    const { projects } = useGetProjects({ staleTime: STALE_TIME });
    const assignedProjectsId = inscriptionData[PROJECT_ID];
    const assignedProjects: Project[] = filterAssignedProjects(
        projects,
        assignedProjectsId,
    );
    const assignedProjectsNames = assignedProjects
        .map((project) => project.name)
        .join(', ');

    return (
        <Box sx={{ marginTop: '1rem' }}>
            <Typography variant="body1Bold">
                {t('budgetHistory.overview')}
            </Typography>
            <StyAmounts>
                <StyTextField
                    disabled
                    fullWidth
                    label={t('budgetProposal.yearAmount', {
                        year: budgetProposal.exercice - 1,
                    })}
                    defaultValue={
                        comparisonMetricAmount &&
                        comparisonMetricAmount !== 'unknown'
                            ? comparisonMetricAmount
                            : t('budgetProposal.newInscription')
                    }
                    variant="outlined"
                    margin="dense"
                    size="small"
                />
                <StyTextField
                    disabled
                    fullWidth
                    label={translateBlockValue(phaseMetric, true)}
                    defaultValue={phaseMetricAmount}
                    variant="outlined"
                    margin="dense"
                    size="small"
                />
            </StyAmounts>
            <StyTextField
                disabled
                fullWidth
                // TODO StrictNullCheck : Try to remove "!"
                label={capitalize(translateBlockValue(phaseComment!, true))}
                defaultValue={phaseCommentValue}
                variant="outlined"
                margin="dense"
                size="small"
            />
            <StyTextField
                disabled
                fullWidth
                label={t('budgetProposal.projects')}
                defaultValue={assignedProjectsNames}
                variant="outlined"
                margin="dense"
                size="small"
                multiline
            />
        </Box>
    );
}
